import type { WatchedRange, WatchedView } from '@kidshub/contracts';

/**
 * Reading a video's viewings back: which moments were watched how often,
 * which were watched again, and where the child walked away.
 *
 * Everything comes from the ranges a tablet reports. A seek is the gap between
 * one range's end and the next one's start, so it is not stored and does not
 * need to be: it is read here.
 */

/** Slices the timeline is counted in. Fine enough for a ten-minute episode. */
export const BUCKET_SECONDS = 5;

export interface Coverage {
  /** Per bucket: how many times that stretch was on screen, rewatches included. */
  counts: number[];
  /** Per bucket: how many of those were a second (or later) pass in the same viewing. */
  repeats: number[];
  /** Per bucket: viewings that ended there because the child left. */
  exits: number[];
  /** The stretch rewound to most, or null if nothing was watched twice. */
  rewatched: { from: number; to: number; times: number } | null;
  /** Where the child most often left, or null if she never did. */
  leftMost: { at: number; times: number } | null;
}

/** Leaving the video and leaving the app read the same from the parent's side. */
export const isWalkAway = (view: WatchedView): boolean =>
  view.end?.reason === 'LEFT' || view.end?.reason === 'BACKGROUND';

export const coverage = (views: WatchedView[], durationSeconds: number): Coverage => {
  const size = Math.max(1, Math.ceil(durationSeconds / BUCKET_SECONDS));
  const counts = new Array<number>(size).fill(0);
  const repeats = new Array<number>(size).fill(0);
  const exits = new Array<number>(size).fill(0);
  const clamp = (bucket: number) => Math.min(size - 1, Math.max(0, bucket));

  for (const view of views) {
    const seen = new Array<boolean>(size).fill(false);
    for (const [from, to] of view.ranges) {
      for (
        let bucket = clamp(Math.floor(from / BUCKET_SECONDS));
        bucket < Math.min(size, Math.ceil(to / BUCKET_SECONDS));
        bucket += 1
      ) {
        counts[bucket] = (counts[bucket] ?? 0) + 1;
        if (seen[bucket]) {
          repeats[bucket] = (repeats[bucket] ?? 0) + 1;
        }
        seen[bucket] = true;
      }
    }

    const last = view.ranges[view.ranges.length - 1];
    if (last !== undefined && isWalkAway(view)) {
      const bucket = clamp(Math.floor(last[1] / BUCKET_SECONDS));
      exits[bucket] = (exits[bucket] ?? 0) + 1;
    }
  }

  return { counts, repeats, exits, rewatched: peakStretch(repeats), leftMost: peakPoint(exits) };
};

/** The longest run of buckets at the highest repeat count, as seconds. */
const peakStretch = (repeats: number[]): Coverage['rewatched'] => {
  const top = Math.max(0, ...repeats);
  if (top === 0) {
    return null;
  }

  let best: { start: number; length: number } | null = null;
  let start = -1;
  repeats.forEach((value, bucket) => {
    if (value === top && start === -1) {
      start = bucket;
    }
    const ends = value !== top || bucket === repeats.length - 1;
    if (start !== -1 && ends) {
      const end = value === top ? bucket + 1 : bucket;
      if (best === null || end - start > best.length) {
        best = { start, length: end - start };
      }
      start = -1;
    }
  });

  const found = best as { start: number; length: number } | null;
  return found === null
    ? null
    : {
        from: found.start * BUCKET_SECONDS,
        to: (found.start + found.length) * BUCKET_SECONDS,
        times: top + 1,
      };
};

const peakPoint = (exits: number[]): Coverage['leftMost'] => {
  const top = Math.max(0, ...exits);
  return top === 0 ? null : { at: exits.indexOf(top) * BUCKET_SECONDS, times: top };
};

export interface Seek {
  from: number;
  to: number;
}

/** The jumps between consecutive stretches of one viewing. */
export const seeksOf = (ranges: WatchedRange[]): Seek[] =>
  ranges.slice(1).flatMap((range, index) => {
    const previous = ranges[index];
    return previous === undefined || Math.abs(range[0] - previous[1]) < 1
      ? []
      : [{ from: previous[1], to: range[0] }];
  });

/** Seconds on screen in one viewing, rewatches included. */
export const watchedSeconds = (ranges: WatchedRange[]): number =>
  ranges.reduce((sum, [from, to]) => sum + Math.max(0, to - from), 0);

/** Whether a range, in its viewing, covers ground the child had already seen. */
export const isRewatch = (ranges: WatchedRange[], index: number): boolean => {
  const range = ranges[index];
  return (
    range !== undefined &&
    ranges.slice(0, index).some(([from, to]) => range[0] < to && range[1] > from)
  );
};
